/**
 * Field drivers as the web names and treats them (the platform registry is
 * `GET /v1/things/drivers`; ids are the wire contract with GatewayPro).
 *
 * The drivers list is the source of truth for everything it carries (label,
 * medium, default port, browse kind, schemas, availability). The fallbacks here
 * only cover a driver list that failed to load or a server older than a field,
 * so a discover hit can still be labelled and routed.
 */

import type { DriverBrowseKind, DriverInfo } from "@/lib/api/things";

export const DRIVER = {
  MODBUS_TCP: "modbus-tcp",
  MODBUS_RTU: "modbus-rtu",
  OPCUA: "opcua",
  S7: "s7",
  ENIP: "enip",
  MELSEC: "melsec",
  RESULTS_FEED: "results-feed",
} as const;

/** True for the two Modbus drivers (they share the register-range picker and address form). */
export function isModbusDriver(id: string | null | undefined): boolean {
  return id === DRIVER.MODBUS_TCP || id === DRIVER.MODBUS_RTU;
}

const SHORT_LABEL: Record<string, string> = {
  "modbus-tcp": "Modbus TCP",
  "modbus-rtu": "Modbus RTU",
  opcua: "OPC UA",
  s7: "Siemens S7",
  enip: "EtherNet/IP",
  melsec: "MELSEC",
  "results-feed": "Results feed",
};

/** A short protocol name for chips, badges and default device names. */
export function driverLabel(id: string, drivers?: DriverInfo[] | null): string {
  return SHORT_LABEL[id] ?? drivers?.find((d) => d.id === id)?.label ?? id;
}

/**
 * Where the gateway will actually connect, from the link the Protocol step built
 * ("10.2.10.114:44819", an OPC UA endpoint, or a serial port), falling back to the
 * discovered device. Shown in the Points headers and error messages, so an edited
 * port (or a host left unchanged) is visible before a read times out.
 */
export function linkAddress(
  link: Record<string, unknown>,
  fallback: { ip?: string; port?: number } = {},
): string {
  if (typeof link.endpoint === "string" && link.endpoint) return link.endpoint.replace(/^opc\.tcp:\/\//, "");
  if (typeof link.serial_port === "string" && link.serial_port) return link.serial_port;
  const host = typeof link.host === "string" && link.host ? link.host : (fallback.ip ?? "");
  const port = typeof link.port === "number" && link.port > 0 ? link.port : fallback.port;
  if (!host) return "the device";
  return port ? `${host}:${port}` : host;
}

/**
 * Link fields a connection form folds under "Advanced settings": the ones
 * almost every device leaves at the default (S7 connection type, the MELSEC
 * routing numbers, OPC UA security, the EtherNet/IP route path). A schema can
 * mark more with `x-ui-advanced`; a required field is never folded.
 */
const LINK_ADVANCED: Record<string, readonly string[]> = {
  s7: ["conn_type"],
  melsec: ["network", "pc", "io", "station"],
  opcua: ["security_policy", "security_mode"],
  enip: ["path"],
  "modbus-rtu": ["data_bits", "stop_bits"],
};

export function linkAdvancedFields(driverId: string | null | undefined): readonly string[] {
  return (driverId && LINK_ADVANCED[driverId]) || [];
}

const DEFAULT_PORT: Record<string, number> = {
  "modbus-tcp": 502,
  opcua: 4840,
  s7: 102,
  enip: 44818,
  melsec: 5007,
  "results-feed": 8091,
};

/** The port an Ethernet driver listens on by default (the driver list's, else the registry's known one). */
export function driverDefaultPort(id: string, drivers?: DriverInfo[] | null): number | undefined {
  return drivers?.find((d) => d.id === id)?.default_port ?? DEFAULT_PORT[id];
}

/** The host a driver's device sits on when it is known: a results feed is an app on the gateway itself. */
const DEFAULT_HOST: Record<string, string> = {
  [DRIVER.RESULTS_FEED]: "127.0.0.1",
};

export function driverDefaultHost(id: string): string {
  return DEFAULT_HOST[id] ?? "";
}

/**
 * The manual-add address after the protocol changes: the new driver's default host when the field is empty or still
 * holds the old driver's default, otherwise what was typed.
 */
export function manualHostAfterDriverChange(current: string, from: string, to: string): string {
  const typed = current.trim();
  return typed === "" || typed === driverDefaultHost(from) ? driverDefaultHost(to) : current;
}

const KNOWN_BROWSE: ReadonlySet<string> = new Set([
  "register-range",
  "node-tree",
  "tag-list",
  "block-scan",
  "none",
]);

/**
 * Which point picker a driver gets. The drivers list decides; a kind this build
 * does not know is manual entry. Without a list entry the registry's defaults
 * apply (S7 and MELSEC are `block-scan` from the drivers-beta backend on; an
 * older server says `none`, which the list then carries).
 */
export function browseKindOf(id: string, drivers?: DriverInfo[] | null): DriverBrowseKind {
  const listed = drivers?.find((d) => d.id === id)?.browse;
  if (listed !== undefined) return KNOWN_BROWSE.has(listed) ? (listed as DriverBrowseKind) : "none";
  switch (id) {
    case DRIVER.MODBUS_TCP:
    case DRIVER.MODBUS_RTU:
      return "register-range";
    case DRIVER.OPCUA:
      return "node-tree";
    case DRIVER.ENIP:
      return "tag-list";
    case DRIVER.S7:
    case DRIVER.MELSEC:
      return "block-scan";
    default:
      return "none";
  }
}

/**
 * Why a device on this gateway cannot use a driver, in plain words (the
 * backend's own sentences, `driverUnavailableMessage` in instruments_v2.go,
 * made operator-facing).
 */
export function unavailableReasonText(reason: string | null | undefined, label: string): string {
  switch (reason) {
    case "not_in_product":
      return `This gateway's product does not include ${label}.`;
    case "planned":
      return `${label} is not released for gateways yet.`;
    case "no_birth":
      return "The gateway has not announced what it supports yet. Start it on GatewayPro v0.3 or later, then try again.";
    case "no_serial_port":
      return "The gateway reported no serial port.";
    case "driver_not_advertised":
      return `The gateway's firmware does not include ${label}.`;
    case "firmware_too_old":
      return `The gateway's firmware is too old for ${label}. Update it to GatewayPro v0.3 or later.`;
    case null:
    case undefined:
    case "":
      return "";
    default:
      return `Not available on this gateway (${reason}).`;
  }
}

/** A driver's status as a short badge word, or "" for a generally available one. */
export function driverStatusBadge(status: string): string {
  if (status === "beta") return "Beta";
  if (status === "planned") return "Planned";
  return "";
}

/** One protocol chip on the Discover step. */
export interface DiscoverChip {
  id: string;
  label: string;
  /** False when this gateway cannot run the driver at all (the chip shows `note` and cannot be ticked). */
  enabled: boolean;
  /** Ticked by default: the drivers a new device on this gateway may use. */
  preselected: boolean;
  /** Why the chip is limited or off, in words; undefined for a plainly available driver. */
  note?: string;
}

/**
 * The Discover step's protocol chips: every Ethernet driver that is not
 * `planned` (a scan sweeps a network, so serial drivers never apply), except
 * the results feed: an app's feed on the gateway itself has nothing to find.
 *
 * `available` answers "may a DEVICE on this gateway use the driver", and its
 * first check is the product. Discover itself does not look at the product
 * (the gateway's birth decides what it can probe), so a driver unavailable
 * only as `not_in_product` can still be scanned: it is offered unticked, with
 * a note, as the Android app does. Every other reason means the gateway cannot
 * run it, and the chip is off.
 */
export function discoverChips(drivers: DriverInfo[] | null | undefined): DiscoverChip[] {
  return (drivers ?? [])
    .filter((d) => d.medium === "ethernet" && d.status !== "planned" && d.id !== DRIVER.RESULTS_FEED)
    .map((d) => {
      const label = driverLabel(d.id, drivers);
      if (d.available) return { id: d.id, label, enabled: true, preselected: true };
      if (d.unavailable_reason === "not_in_product") {
        return {
          id: d.id,
          label,
          enabled: true,
          preselected: false,
          note: `Not in this gateway's product: it can be scanned, not added as a device.`,
        };
      }
      return { id: d.id, label, enabled: false, preselected: false, note: unavailableReasonText(d.unavailable_reason, label) };
    });
}

/**
 * The `drivers` to send with a discover. Only Modbus TCP ticked (or no chips:
 * a server without the drivers list, a pre-v2 gateway) sends none, which is
 * exactly today's request, so old gateways see no change.
 */
export function discoverDriversParam(picked: Iterable<string>): string[] | undefined {
  const list = [...picked];
  if (list.length === 0 || (list.length === 1 && list[0] === DRIVER.MODBUS_TCP)) return undefined;
  return list;
}

/**
 * A one-line summary of what a device said about itself: product or module,
 * vendor, the OPC UA endpoint, the Modbus unit, the S7 rack / slot and a
 * sample reading. "" when it reported nothing.
 */
export function identitySummary(identity: Record<string, string>, unitId?: number): string {
  const parts: string[] = [];
  const product = identity.product || identity.module_name || identity.model;
  if (product) parts.push(product);
  const vendor = identity.vendor;
  if (vendor && !parts.some((p) => p.toLowerCase().includes(vendor.toLowerCase()))) parts.push(vendor);
  if (identity.endpoint) parts.push(identity.endpoint);
  const unit = identity.unit_id || (unitId && unitId > 0 ? String(unitId) : "");
  if (unit) parts.push(`unit ${unit}`);
  if (identity.rack !== undefined && identity.slot !== undefined) parts.push(`rack ${identity.rack} / slot ${identity.slot}`);
  if (identity.frame) parts.push(`${identity.frame} frame`);
  if (identity.sample_value !== undefined) {
    parts.push(identity.sample_from ? `${identity.sample_from} = ${identity.sample_value}` : `reads ${identity.sample_value}`);
  }
  return parts.join(" · ");
}

/** One entry of the Protocol step: every driver, the unavailable ones with the reason. */
export interface ProtocolChoice {
  id: string;
  label: string;
  medium: string;
  status: string;
  available: boolean;
  /** Plain-language reason when not available. */
  reason: string;
}

export function protocolChoices(drivers: DriverInfo[] | null | undefined): ProtocolChoice[] {
  return (drivers ?? []).map((d) => {
    const label = driverLabel(d.id, drivers);
    return {
      id: d.id,
      label,
      medium: d.medium,
      status: d.status,
      available: d.available,
      reason: d.available ? "" : unavailableReasonText(d.unavailable_reason, label),
    };
  });
}

/**
 * The protocol the Protocol step starts on: the discovered device's driver
 * whenever the registry lists it, even when this gateway may not use it (the
 * step then says why and holds Next, rather than silently switching protocol);
 * else the first available driver; else the device's own.
 */
export function preselectDriver(deviceDriver: string, drivers: DriverInfo[] | null | undefined): string {
  const list = drivers ?? [];
  if (list.length === 0 || list.some((d) => d.id === deviceDriver)) return deviceDriver;
  return list.find((d) => d.available)?.id ?? deviceDriver;
}

/** What a device's connection form starts from: the discover hit's address and identity. */
export interface LinkPrefillSource {
  ip: string;
  port?: number;
  unit_id: number;
  identity: Record<string, string>;
}

/**
 * The link params a discovered (or keyed-in) device prefills, named as each
 * driver's link_schema names them: Modbus TCP {host, port, unit_id}; Modbus
 * RTU {unit_id} (the serial port comes from the birth); OPC UA {endpoint}, the
 * one the server reported, else opc.tcp://ip:port; S7 {host, port, rack,
 * slot}, the identity's rack / slot when reported; EtherNet/IP {host, port};
 * MELSEC {host, port} plus the frame and format the device answered on.
 * A port is only set when the scan found one.
 */
export function linkPrefill(driver: string, d: LinkPrefillSource): Record<string, unknown> {
  const hostPort = (): Record<string, unknown> => ({
    host: d.ip,
    ...(d.port ? { port: d.port } : {}),
  });
  const int = (v: string | undefined, lo: number, hi: number): number | undefined => {
    const n = Number(v);
    return v !== undefined && v !== "" && Number.isInteger(n) && n >= lo && n <= hi ? n : undefined;
  };
  switch (driver) {
    case DRIVER.MODBUS_TCP:
      return { ...hostPort(), unit_id: d.unit_id > 0 ? d.unit_id : (int(d.identity.unit_id, 1, 247) ?? 1) };
    case DRIVER.MODBUS_RTU:
      return { unit_id: d.unit_id > 0 ? d.unit_id : 1 };
    case DRIVER.OPCUA: {
      const reported = d.identity.endpoint;
      const endpoint =
        reported && reported.startsWith("opc.tcp://")
          ? reported
          : d.ip
            ? `opc.tcp://${d.ip}:${d.port ?? driverDefaultPort(DRIVER.OPCUA)}`
            : "";
      return endpoint ? { endpoint } : {};
    }
    case DRIVER.S7: {
      const rack = int(d.identity.rack, 0, 7);
      const slot = int(d.identity.slot, 0, 31);
      return { ...hostPort(), ...(rack !== undefined ? { rack } : {}), ...(slot !== undefined ? { slot } : {}) };
    }
    case DRIVER.MELSEC: {
      const frame = d.identity.frame?.toUpperCase();
      const format = d.identity.format?.toLowerCase();
      return {
        ...hostPort(),
        ...(frame === "3E" || frame === "4E" ? { frame } : {}),
        ...(format === "binary" || format === "ascii" ? { format } : {}),
      };
    }
    default:
      return d.ip ? hostPort() : {};
  }
}

/**
 * Whether a gateway product polls field devices, so it gets the instruments
 * panel instead of its own tag rows: any product with drivers (web brief U5).
 * A server older than `Product.drivers` falls back to today's rule, a Modbus
 * field protocol.
 */
export function productPollsDevices(
  p: { drivers?: string[] | null; field_protocol?: string | null } | null | undefined,
): boolean {
  if (!p) return false;
  if (Array.isArray(p.drivers)) return p.drivers.length > 0;
  return /modbus/i.test(p.field_protocol ?? "");
}

/**
 * The product to offer when a gateway's firmware runs more drivers than its
 * product lets it use (e.g. a GatewayPro commissioned as a modbus-tcp gateway
 * that advertises all six): the catalogue product whose drivers cover every
 * driver the birth advertises and are more than the current product's,
 * preferring `gatewaypro`. Null when the birth advertises nothing new or no
 * product covers it (e.g. a server whose catalogue has no gatewaypro yet).
 */
export function productSwitchCandidate<P extends { id: string; drivers?: string[] | null }>(
  current: P | null | undefined,
  products: P[],
  advertised: string[],
): P | null {
  const have = new Set(current?.drivers ?? []);
  if (advertised.length === 0 || advertised.every((d) => have.has(d))) return null;
  const covering = products.filter(
    (p) =>
      p.id !== current?.id &&
      Array.isArray(p.drivers) &&
      p.drivers.length > have.size &&
      advertised.every((d) => p.drivers!.includes(d)),
  );
  return covering.find((p) => p.id === "gatewaypro") ?? covering[0] ?? null;
}
